import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { appConfig } from '../../config/configManager';
import { useUserStore } from '../../contexts/StoreContext';
import { AccountUserStorage } from '../../storage/AccountUserStorage';
import type { AccountRedeemGiftDto, AccountRedeemGiftResponseDto, AccountValidateGiftDto, AccountValidateGiftResponseDto } from '../../types/api/service';

// ─── Dev helpers ──────────────────────────────────────────────────────────────
const isDev = (): boolean =>
  typeof __DEV__ !== 'undefined' ? __DEV__ : (globalThis as any).process?.env?.NODE_ENV === 'development';

// ─── Purchase config ────────────────────────────────────────────────────────
// Web Stripe checkout is backend-driven via account_create_stripe_checkout
// (BPProcId=2, BPProdList=15 annual / 16 lifetime).
// Mobile IAP SKUs must match App Store Connect / Google Play Console exactly.
const ANNUAL_SKU = 'com.childnav.family.annual';
const LIFETIME_SKU = 'com.childnav.family.lifetime';
// ─────────────────────────────────────────────────────────────────────────────

interface UpgradeViewProps {
  onBack: () => void;
  apiService?: any;
  iapService?: any;  // IAPService instance — present on mobile, undefined on web
  storage?: any;     // StorageInterface — needed for gift code local storage update
}

interface PlanCardProps {
  badge?: string;
  title: string;
  price: string;
  priceDetail: string;
  description: string;
  bullets: string[];
  ctaLabel: string;
  onPurchase: () => void;
  highlighted?: boolean;
  footer?: React.ReactNode;
}

const PlanCard: React.FC<PlanCardProps> = ({
  badge,
  title,
  price,
  priceDetail,
  description,
  bullets,
  ctaLabel,
  onPurchase,
  highlighted = false,
  footer,
}) => (
  <View style={[styles.card, highlighted && styles.cardHighlighted]}>
    {badge ? (
      <View style={styles.badge}>
        <Text style={styles.badgeText}>{badge}</Text>
      </View>
    ) : null}
    <Text style={[styles.cardTitle, highlighted && styles.cardTitleHighlighted]}>{title}</Text>
    <Text style={[styles.cardPrice, highlighted && styles.cardPriceHighlighted]}>{price}</Text>
    <Text style={styles.cardPriceDetail}>{priceDetail}</Text>
    <Text style={styles.cardDescription}>{description}</Text>
    <View style={styles.bulletList}>
      {bullets.map((b, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={styles.bulletCheck}>✓</Text>
          <Text style={styles.bulletText}>{b}</Text>
        </View>
      ))}
    </View>
    <TouchableOpacity
      style={[styles.purchaseBtn, highlighted && styles.purchaseBtnHighlighted]}
      onPress={onPurchase}
      activeOpacity={0.85}
    >
      <Text style={styles.purchaseBtnText}>{ctaLabel}</Text>
    </TouchableOpacity>
    {footer}
  </View>
);

export const UpgradeView: React.FC<UpgradeViewProps> = ({ onBack, apiService, iapService, storage }) => {
  const userStore = useUserStore();
  const accountId = userStore ? userStore((state: any) =>
    state.session?.user?.familyId || state.session?.familyAccount?.familyId || ''
  ) : '';
  const accountEmail = userStore ? userStore((state: any) =>
    state.session?.user?.email || ''
  ) : '';

  // ── Purchase state ─────────────────────────────────────────────────────────
  const [purchasing, setPurchasing] = React.useState(false);
  const [restoring, setRestoring] = React.useState(false);

  // ── Debug panel state (DEV only) ───────────────────────────────────────────
  const [debugInfo, setDebugInfo] = React.useState<Record<string, string>>({});
  const [simulating, setSimulating] = React.useState(false);
  React.useEffect(() => {
    if (!isDev()) return;
    const info = iapService?.getDebugInfo?.() ?? {};
    setDebugInfo({
      platform: Platform.OS,
      iapConnected: info.initialized ? 'YES' : 'NO',
      iapInitError: info.initError || '—',
      accountId: info.accountId || accountId || '(none)',
      email: info.email || accountEmail || '(none)',
    });
  }, [iapService, accountId, accountEmail]);

  // ── Gift code state ────────────────────────────────────────────────────────
  const [giftCode, setGiftCode] = React.useState('');
  const [giftError, setGiftError] = React.useState<string | null>(null);
  const [giftSuccess, setGiftSuccess] = React.useState<string | null>(null);
  const [redeemingGift, setRedeemingGift] = React.useState(false);
  // Validated code: set after step-1 validation succeeds, cleared when input changes
  const [validatedCode, setValidatedCode] = React.useState<{ code: string; value: number; type: string } | null>(null);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const applyPremiumLocally = async (responseDto: { CustomerId: string; SubscriptionId: string; ProductList: string }) => {
    appConfig.updateConfig({ tier: 'premium' });
    if (accountId && storage) {
      try {
        const accountUserStorage = new AccountUserStorage(storage);
        const dto = await accountUserStorage.getAccountGetInfo(accountId);
        if (dto) {
          await accountUserStorage.saveAccountGetInfo(accountId, {
            ...dto,
            IsPremium: '1',
            CustomerId: responseDto.CustomerId,
            SubscriptionId: responseDto.SubscriptionId,
            ProductList: responseDto.ProductList,
          });
        }
      } catch (e) {
        console.warn('⚠️ UpgradeView: failed to update local storage after premium activation', e);
      }
    }
  };

  // ── Purchase handler ───────────────────────────────────────────────────────
  const handlePurchase = async (planKey: 'annual' | 'lifetime') => {
    if (Platform.OS === 'web') {
      // Web → Stripe Checkout Session via backend (AccountStripeCheckoutDto)
      if (apiService) {
        try {
          const origin = window.location.origin;
          const checkoutPayload = {
            Email: accountEmail,
            BPProcId: '2',
            BPProdList: planKey === 'annual' ? '15' : '16',
            BPInfo: '',
            SuccessURL: `${origin}/?upgrade=success&session_id={CHECKOUT_SESSION_ID}`,
            CancelURL: `${origin}/?upgrade=cancel`,
          };
          const response = await apiService.processServiceRequestWithRefresh({
            RequestType: 'account_create_stripe_checkout',
            Payload: JSON.stringify(checkoutPayload),
          }, 'sync', { userStore });
          if (!response?.success) {
            throw new Error(response?.message || 'Backend returned an error');
          }
          let checkoutDto: { CheckoutId?: string; CheckoutURL?: string } = {};
          if (typeof response.payload === 'string') {
            checkoutDto = JSON.parse(response.payload);
          } else if (response.payload) {
            checkoutDto = response.payload;
          }
          if (checkoutDto.CheckoutURL) {
            window.location.href = checkoutDto.CheckoutURL;
            return;
          }
        } catch (e) {
          console.warn('⚠️ UpgradeView: account_create_stripe_checkout failed', e);
        }
      }
      Alert.alert('Checkout Unavailable', 'Unable to start checkout. Please try again later.');
    } else {
      // Mobile → react-native-iap via IAPService
      if (!iapService) {
        Alert.alert('Not Available', 'In-app purchasing is not available right now. Please try again.');
        return;
      }
      setPurchasing(true);
      iapService.setOnPurchaseResult((result: { success: boolean; message?: string }) => {
        setPurchasing(false);
        iapService.setOnPurchaseResult(null);
        if (result.success) {
          Alert.alert(
            'Purchase Successful',
            'Welcome to Premium! All premium features are now unlocked.',
            [{ text: 'OK', onPress: onBack }],
          );
        } else {
          Alert.alert('Purchase Failed', result.message || 'Something went wrong. Please try again.');
        }
      });
      try {
        if (planKey === 'annual') {
          await iapService.purchaseSubscription();
        } else {
          await iapService.purchaseLifetime();
        }
      } catch (e: any) {
        setPurchasing(false);
        iapService.setOnPurchaseResult(null);
        // User cancelled = code E_USER_CANCELLED — don't show an error for that
        if (e?.code !== 'E_USER_CANCELLED') {
          Alert.alert('Purchase Error', e?.message || 'Unable to start purchase. Please try again.');
        }
      }
    }
  };

  // ── Restore purchases (mobile only) ────────────────────────────────────────
  const handleRestore = async () => {
    if (!iapService) return;
    setRestoring(true);
    const result = await iapService.restorePurchases();
    setRestoring(false);
    Alert.alert(
      result.success ? 'Purchases Restored' : 'Nothing to Restore',
      result.message || '',
      [{ text: 'OK', onPress: result.success ? onBack : undefined }],
    );
  };

  // ── DEV: Simulate purchase (bypasses store + backend) ──────────────────────
  const handleSimulatePurchase = async (sku: string) => {
    if (!iapService) {
      Alert.alert('Dev Sim', 'iapService is not available — check App.tsx wiring.');
      return;
    }
    setSimulating(true);
    iapService.setOnPurchaseResult((result: { success: boolean; message?: string }) => {
      setSimulating(false);
      iapService.setOnPurchaseResult(null);
      Alert.alert(
        result.success ? '✅ Sim Success' : '❌ Sim Failed',
        result.message || '',
        [{ text: 'OK', onPress: result.success ? onBack : undefined }],
      );
    });
    await iapService.simulatePurchase(sku);
  };

  // ── Gift / promo code: step 1 — validate ──────────────────────────────────
  const handleValidateGift = async () => {
    const code = giftCode.trim().toUpperCase();
    if (!code) {
      setGiftError('Please enter a gift reward code.');
      return;
    }
    if (!apiService) {
      setGiftError('Service unavailable. Please try again later.');
      return;
    }
    setGiftError(null);
    setGiftSuccess(null);
    setValidatedCode(null);
    setRedeemingGift(true);
    try {
      const dto: AccountValidateGiftDto = { GiftCode: code };
      const response = await apiService.processServiceRequestWithRefresh({
        RequestType: 'account_validate_gift',
        Payload: JSON.stringify(dto),
      }, 'sync', { userStore });

      if (!response?.success) {
        setGiftError(response?.message || 'Invalid or expired code. Please check and try again.');
        return;
      }
      let validateDto: AccountValidateGiftResponseDto = { Value: '' };
      if (typeof response.payload === 'string') {
        validateDto = JSON.parse(response.payload);
      } else if (response.payload) {
        validateDto = response.payload;
      }
      const codeValue = parseFloat(validateDto.Value) || 0;
      if (codeValue <= 0) {
        setGiftError('Code is not valid or has already been redeemed.');
        return;
      }
      setValidatedCode({ code, value: codeValue, type: 'gift' });
      const lifetimePrice = 19.99;
      if (codeValue >= lifetimePrice) {
        setGiftSuccess(`Full lifetime Premium code — no charge required. Tap "Redeem" to activate.`);
      } else {
        const remaining = Math.max(0, lifetimePrice - codeValue).toFixed(2);
        setGiftSuccess(`$${codeValue.toFixed(2)} reward applied — $${remaining} remaining toward lifetime Premium.`);
      }
    } catch (e: any) {
      setGiftError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setRedeemingGift(false);
    }
  };

  // ── Gift / promo code: step 2 — proceed ────────────────────────────────────
  const handleProceedWithGift = async () => {
    if (!validatedCode || !apiService) return;
    const { code, value } = validatedCode;
    const lifetimePrice = 19.99;
    const isFull = value >= lifetimePrice;

    setGiftError(null);
    setRedeemingGift(true);
    try {
      if (isFull) {
        // Full-price code — no payment needed on any platform
        const redeemDto: AccountRedeemGiftDto = { GiftCode: code, AccountId: accountId, Email: accountEmail };
        const response = await apiService.processServiceRequestWithRefresh({
          RequestType: 'account_redeem_gift',
          Payload: JSON.stringify(redeemDto),
        }, 'sync', { userStore });
        if (!response?.success) {
          setGiftError(response?.message || 'Redemption failed. Please try again.');
          return;
        }
        let responseDto: AccountRedeemGiftResponseDto = { CustomerId: '', SubscriptionId: '', ProductList: '', Message: '' };
        if (typeof response.payload === 'string') {
          responseDto = JSON.parse(response.payload);
        } else if (response.payload) {
          responseDto = response.payload;
        }
        await applyPremiumLocally(responseDto);
        setGiftSuccess(responseDto.Message || 'Your account has been upgraded to Premium!');
        setTimeout(() => onBack(), 2000);
      } else if (Platform.OS === 'web') {
        // Partial code on web — Stripe checkout with embedded discount
        const origin = window.location.origin;
        const checkoutPayload = {
          Email: accountEmail,
          BPProcId: '2',
          BPProdList: '16', // lifetime only
          BPInfo: '',
          GiftCode: code,
          SuccessURL: `${origin}/?upgrade=success&session_id={CHECKOUT_SESSION_ID}`,
          CancelURL: `${origin}/?upgrade=cancel`,
        };
        const response = await apiService.processServiceRequestWithRefresh({
          RequestType: 'account_create_stripe_checkout',
          Payload: JSON.stringify(checkoutPayload),
        }, 'sync', { userStore });
        if (!response?.success) {
          setGiftError(response?.message || 'Unable to start checkout. Please try again.');
          return;
        }
        let checkoutDto: { CheckoutURL?: string } = {};
        if (typeof response.payload === 'string') {
          checkoutDto = JSON.parse(response.payload);
        } else if (response.payload) {
          checkoutDto = response.payload;
        }
        if (checkoutDto.CheckoutURL) {
          window.location.href = checkoutDto.CheckoutURL;
          return;
        }
        setGiftError('Unable to start checkout. Please try again.');
      } else {
        // Partial code on mobile — discounted IAP SKU
        if (!iapService) {
          setGiftError('In-app purchasing is not available. Please try again.');
          return;
        }
        // Select discounted SKU by code value:
        // <= $6.99 discount → tier1disc (~$13.00 remaining)
        // <= $12.99 discount → tier2disc (~$7.00 remaining)
        const discountedSku = value <= 6.99
          ? 'com.childnav.family.lifetime.tier1disc'
          : 'com.childnav.family.lifetime.tier2disc';
        const remaining = (lifetimePrice - value).toFixed(2);
        iapService.setPendingGiftCode(code);
        setPurchasing(true);
        iapService.setOnPurchaseResult((result: { success: boolean; message?: string }) => {
          setPurchasing(false);
          iapService.setOnPurchaseResult(null);
          if (result.success) {
            Alert.alert(
              'Purchase Successful',
              'Welcome to Premium! All premium features are now unlocked.',
              [{ text: 'OK', onPress: onBack }],
            );
          } else {
            iapService.setPendingGiftCode(null); // clear on failure
            setGiftError(result.message || 'Purchase failed. Please try again.');
          }
        });
        try {
          await iapService.purchaseDiscountedLifetime(discountedSku);
          // Show info while store dialog is open
          setGiftSuccess(`Opening store for $${remaining} balance — code will be applied automatically.`);
        } catch (e: any) {
          setPurchasing(false);
          iapService.setOnPurchaseResult(null);
          iapService.setPendingGiftCode(null);
          if (e?.code !== 'E_USER_CANCELLED') {
            setGiftError(e?.message || 'Unable to start purchase. Please try again.');
          } else {
            setGiftSuccess(null); // user cancelled — reset info
          }
        }
      }
    } catch (e: any) {
      setGiftError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setRedeemingGift(false);
    }
  };

  // ── Derive proceed button label ─────────────────────────────────────────────
  const getProceedLabel = (): string => {
    if (!validatedCode) return 'Redeem';
    const { value } = validatedCode;
    const lifetimePrice = 19.99;
    if (value >= lifetimePrice) return 'Redeem — No Charge';
    const remaining = (lifetimePrice - value).toFixed(2);
    if (Platform.OS === 'web') return `Proceed to Checkout ($${remaining})`;
    return `Continue to Purchase ($${remaining})`;
  };

  // ── Derived display prices ──────────────────────────────────────────────────
  const annualPrice = '$9.99';
  const lifetimePrice = '$19.99';
  const annualCtaLabel = 'Subscribe — $9.99/yr';
  const lifetimeCtaLabel = 'Buy Now — $19.99';

  return (
    <View style={styles.backgroundContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
        <View style={{ maxWidth: appConfig.getTwoCardMaxWidth(), alignSelf: 'center', width: '100%', paddingHorizontal: appConfig.getCardMargin() }}>
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity onPress={onBack} style={styles.backBtn}>
              <Text style={styles.backBtnText}>← Back</Text>
            </TouchableOpacity>
            <Text style={styles.heading}>Upgrade to Premium</Text>
            <Text style={styles.subheading}>
              Unlock every developmental domain, advanced analytics, detailed reports, and unlimited family sharing.
            </Text>
          </View>

          {/* Plan cards */}
          <View style={styles.cardsRow}>
            <PlanCard
              title="Premium Annual"
              price={annualPrice}
              priceDetail="per year — billed annually"
              description="Full premium access renewed each year. Cancel any time."
              bullets={[
                'Extended CDC milestones',
                'ASQ® equivalent domains',
                'Advanced analytics & insights',
                'Unlimited family access',
                'Expanded profiles',
                'Expanded activities',
                'Expanded assessments',
              ]}
              ctaLabel={purchasing ? 'Processing…' : annualCtaLabel}
              onPurchase={() => !purchasing && handlePurchase('annual')}
            />
            <PlanCard
              badge="Best Value"
              title="Premium Lifetime"
              price={lifetimePrice}
              priceDetail="one-time purchase — yours forever"
              description="Pay once and own Premium on every platform, for life. Gift codes from referral rewards apply here."
              bullets={[
                'Everything in Annual',
                'No recurring charges',
                'All future premium features',
                'Use referral reward gift codes',
                'Cross-platform (web + mobile)',
              ]}
              ctaLabel={purchasing ? 'Processing…' : lifetimeCtaLabel}
              onPurchase={() => !purchasing && handlePurchase('lifetime')}
              highlighted
              footer={
                <View style={{ marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#e2e8f0', gap: 10 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: '#1a1a2e', marginBottom: 2 }}>Have a Gift Reward code?</Text>
                  <View style={styles.giftRow}>
                    <TextInput
                      style={[styles.giftInput, { color: '#1a1a2e', backgroundColor: '#f5f3ff', borderColor: '#c4b5fd' }]}
                      value={giftCode}
                      onChangeText={text => {
                        setGiftCode(text);
                        setGiftError(null);
                        setGiftSuccess(null);
                        setValidatedCode(null);
                      }}
                      placeholder="Enter code"
                      placeholderTextColor="#9ca3af"
                      autoCapitalize="characters"
                      autoCorrect={false}
                      editable={!redeemingGift && !purchasing}
                    />
                    {!validatedCode ? (
                      <TouchableOpacity
                        style={[styles.giftBtn, (redeemingGift || purchasing) && styles.giftBtnDisabled]}
                        onPress={handleValidateGift}
                        disabled={redeemingGift || purchasing}
                        activeOpacity={0.8}
                      >
                        {redeemingGift
                          ? <ActivityIndicator size="small" color="#3b82f6" />
                          : <Text style={styles.giftBtnText}>Apply</Text>}
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={[styles.giftBtn, styles.giftBtnProceed, (redeemingGift || purchasing) && styles.giftBtnDisabled]}
                        onPress={handleProceedWithGift}
                        disabled={redeemingGift || purchasing}
                        activeOpacity={0.8}
                      >
                        {redeemingGift || purchasing
                          ? <ActivityIndicator size="small" color="#ffffff" />
                          : <Text style={[styles.giftBtnText, styles.giftBtnProceedText]}>{getProceedLabel()}</Text>}
                      </TouchableOpacity>
                    )}
                  </View>
                  {giftError ? <Text style={styles.giftError}>{giftError}</Text> : null}
                  {giftSuccess ? <Text style={styles.giftSuccessText}>{giftSuccess}</Text> : null}
                </View>
              }
            />
          </View>

          {/* Restore Purchases (mobile only) */}
          {Platform.OS !== 'web' && (
            <TouchableOpacity
              style={styles.restoreBtn}
              onPress={handleRestore}
              disabled={restoring}
              activeOpacity={0.7}
            >
              {restoring
                ? <ActivityIndicator size="small" color="#ffffff" />
                : <Text style={styles.restoreBtnText}>Restore Previous Purchases</Text>}
            </TouchableOpacity>
          )}


          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              {Platform.OS === 'web'
                ? 'Payments are processed securely via Stripe. You will be redirected to a secure checkout page.'
                : "Purchases are processed through your device's app store and are subject to the store's terms and refund policy."}
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    backgroundColor: '#3b82f6',
    paddingVertical: 16,
  },
  header: {
    marginBottom: 24,
    paddingTop: 8,
  },
  backBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 8,
    marginBottom: 16,
  },
  backBtnText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '500',
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 8,
  },
  subheading: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 20,
  },
  cardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  card: {
    flex: 1,
    minWidth: 260,
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: 20,
    borderWidth: 2,
    borderColor: '#e2e8f0',
    position: 'relative',
  },
  cardHighlighted: {
    borderColor: '#7c3aed',
    shadowColor: '#7c3aed',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 6,
  },
  badge: {
    alignSelf: 'flex-start',
    backgroundColor: '#7c3aed',
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginBottom: 10,
  },
  badgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 4,
  },
  cardTitleHighlighted: {
    color: '#7c3aed',
  },
  cardPrice: {
    fontSize: 32,
    fontWeight: '800',
    color: '#1a1a2e',
    marginBottom: 2,
  },
  cardPriceHighlighted: {
    color: '#7c3aed',
  },
  cardPriceDetail: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 12,
  },
  cardDescription: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 18,
    marginBottom: 14,
    borderTopWidth: 1,
    borderTopColor: '#f1f2f6',
    paddingTop: 12,
  },
  bulletList: {
    marginBottom: 20,
    gap: 6,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  bulletCheck: {
    fontSize: 13,
    color: '#10b981',
    fontWeight: '700',
    lineHeight: 18,
  },
  bulletText: {
    flex: 1,
    fontSize: 13,
    color: '#374151',
    lineHeight: 18,
  },
  purchaseBtn: {
    backgroundColor: '#1a1a2e',
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
  },
  purchaseBtnHighlighted: {
    backgroundColor: '#7c3aed',
  },
  purchaseBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '700',
  },
  footer: {
    marginTop: 24,
    gap: 8,
  },
  footerText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 17,
    textAlign: 'center',
  },
  restoreBtn: {
    alignSelf: 'center',
    marginTop: 16,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  restoreBtnText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
    textDecorationLine: 'underline',
  },
  giftSection: {
    marginTop: 28,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 16,
    gap: 10,
  },
  giftHeading: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 2,
  },
  giftSubheading: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 4,
  },
  giftRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  giftInput: {
    flex: 1,
    height: 44,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 15,
    color: '#ffffff',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  giftBtn: {
    height: 44,
    paddingHorizontal: 18,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 90,
  },
  giftBtnProceed: {
    backgroundColor: '#10b981',
    minWidth: 120,
  },
  giftBtnDisabled: {
    opacity: 0.6,
  },
  giftBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#3b82f6',
  },
  giftBtnProceedText: {
    color: '#ffffff',
  },
  giftError: {
    fontSize: 13,
    color: '#fca5a5',
    lineHeight: 18,
  },
  giftSuccessText: {
    fontSize: 13,
    color: '#6ee7b7',
    lineHeight: 18,
    fontWeight: '600',
  },
  // ── DEV debug panel ────────────────────────────────────────────────────────
  debugPanel: {
    marginTop: 28,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,200,0,0.4)',
    gap: 4,
  },
  debugTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#fbbf24',
    marginBottom: 8,
    letterSpacing: 0.5,
  },
  debugRow: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.75)',
    lineHeight: 16,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  debugKey: {
    color: '#93c5fd',
    fontWeight: '600',
  },
  debugBtnRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  debugBtn: {
    flex: 1,
    paddingVertical: 8,
    backgroundColor: '#fbbf24',
    borderRadius: 6,
    alignItems: 'center',
  },
  debugBtnDisabled: {
    opacity: 0.5,
  },
  debugBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#1c1917',
  },
});
